import { z } from 'zod';
import type { ProductRole } from '../catalogue/roles.js';
import { type Qty, compareQty } from '../quantity/index.js';
import { type Uom, type UomConversion, convert } from '../uom/index.js';

/**
 * Ingredient and allergen statements generated from a recipe (vault Core Model change 10:
 * "ingredient + allergen statement generated from the recipe and signed off by the baker").
 * The generated text is a **starting point**: the baker edits it and signs it off with the
 * recipe version; what was signed off is what labels use. "Free from" claims are not
 * generated (they need evidence, vault change 10).
 * @module
 */

/**
 * Allergen keys a product can declare. The commonly regulated list (the South African
 * labelling regulations' and the EU's major allergens) — UNVERIFIED(sa-regs): check the
 * list and its wording against the current R146 / R429 text before labels print it.
 */
export const ALLERGENS = [
  'gluten',
  'crustaceans',
  'molluscs',
  'eggs',
  'fish',
  'peanuts',
  'soya',
  'milk',
  'tree_nuts',
  'celery',
  'mustard',
  'sesame',
  'sulphites',
  'lupin',
] as const;
/** One of {@link ALLERGENS}. */
export type Allergen = (typeof ALLERGENS)[number];
/** zod schema for an allergen key. */
export const allergenSchema = z.enum(ALLERGENS);
/** zod schema for a product's allergens: distinct keys. */
export const allergensSchema = z
  .array(allergenSchema)
  .refine((a) => new Set(a).size === a.length, 'Allergens must not repeat');

/** How each allergen reads in a statement. */
export const ALLERGEN_LABELS: Readonly<Record<Allergen, string>> = {
  gluten: 'gluten',
  crustaceans: 'crustaceans',
  molluscs: 'molluscs',
  eggs: 'eggs',
  fish: 'fish',
  peanuts: 'peanuts',
  soya: 'soya',
  milk: 'milk',
  tree_nuts: 'tree nuts',
  celery: 'celery',
  mustard: 'mustard',
  sesame: 'sesame',
  sulphites: 'sulphites',
  lupin: 'lupin',
};

/** A product as the generator sees it. */
export interface StatementProduct {
  id: string;
  name: string;
  baseUom: Uom;
  /** Allergens the product itself declares (for a bought ingredient: from its spec). */
  allergens: readonly Allergen[];
  /** Its roles: packaging, supplies and services are not ingredients ({@link isNonFood}). */
  roles?: readonly ProductRole[];
  /** Its recipe (active, else latest draft), for nested ingredients and allergens. */
  recipe?: { lines: readonly { componentId: string; qty: string; uom: Uom }[] } | null;
}

/** A generated statement. */
export interface AllergenStatement {
  /** Top-level ingredient names, heaviest first where comparable. */
  ingredients: string[];
  /** Every allergen from the product, its components and their recipes, in list order. */
  allergens: Allergen[];
  /** True when every line could be weighed (so the order is by mass). */
  orderedByMass: boolean;
  /** Components the generator could not find (named by id). */
  unknownComponents: string[];
  /** `Ingredients: …. Contains: ….` */
  text: string;
}

/**
 * Packaging, supplies and services are not food: a recipe may use a cake box, but the box is
 * not an ingredient on the label (UI5A-2). A product that is also an ingredient, an
 * intermediate or sellable counts as food.
 */
export function isNonFood(roles: readonly ProductRole[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  const food = roles.some((r) => r === 'ingredient' || r === 'intermediate' || r === 'sellable');
  return !food && roles.some((r) => r === 'packaging' || r === 'supply' || r === 'service');
}

/**
 * The allergens in `allergens` that a (signed) statement doesn't name. A signed statement
 * is what labels print (D-064): when a component later gains an allergen, the statement no
 * longer covers the ingredients and is out of date (D-065, CR5-6). An allergen counts as
 * named when its label ("tree nuts") or key appears as a word, in any case.
 */
export function allergensNotNamed(statement: string, allergens: readonly Allergen[]): Allergen[] {
  const text = statement.toLowerCase();
  const named = (word: string) =>
    new RegExp(`(^|[^a-z])${word.replace(/[_ ]/g, '[ _-]?')}([^a-z]|$)`).test(text);
  return allergens.filter((a) => !named(ALLERGEN_LABELS[a]) && !named(a));
}

/** Nested ingredients are shown this deep (then the name alone). */
const MAX_DEPTH = 4;

/**
 * Generates the ingredient + allergen statement for `productId`'s recipe `lines`. Lines
 * are ordered by mass (kg, through the conversions) where every line can be weighed, else
 * by recipe position. An intermediate's own ingredients are listed in brackets. Allergens
 * are collected from the product, its components and everything nested under them (loops
 * are cut, never followed twice).
 */
export function generateAllergenStatement(
  productId: string,
  lines: readonly { componentId: string; qty: string; uom: Uom }[],
  products: ReadonlyMap<string, StatementProduct>,
  conversions: readonly UomConversion[] = [],
): AllergenStatement {
  const found = new Set<Allergen>(products.get(productId)?.allergens ?? []);
  const unknown = new Set<string>();

  const describe = (
    list: readonly { componentId: string; qty: string; uom: Uom }[],
    path: ReadonlySet<string>,
    depth: number,
  ): { names: string[]; byMass: boolean } => {
    // Non-food lines (packaging…) are no ingredient, but their allergens still count.
    for (const line of list) {
      const p = products.get(line.componentId);
      if (p && isNonFood(p.roles)) for (const a of p.allergens) found.add(a);
    }
    const food = list.filter((line) => !isNonFood(products.get(line.componentId)?.roles));
    const weighed = food.map((line, i) => {
      const kg = convert(line.qty, line.uom, 'kg', conversions, { productId: line.componentId });
      return { line, i, kg: kg.ok ? kg.qty : null };
    });
    const byMass = weighed.every((w) => w.kg !== null);
    if (byMass) {
      weighed.sort((a, b) => compareQty(b.kg as Qty, a.kg as Qty) || a.i - b.i);
    }
    const names: string[] = [];
    const seen = new Set<string>();
    for (const { line } of weighed) {
      const p = products.get(line.componentId);
      if (!p) {
        unknown.add(line.componentId);
        continue;
      }
      for (const a of p.allergens) found.add(a);
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      const nested = p.recipe?.lines ?? [];
      if (nested.length > 0 && !path.has(p.id)) {
        const inner = describe(nested, new Set([...path, p.id]), depth + 1);
        names.push(
          depth < MAX_DEPTH && inner.names.length > 0
            ? `${p.name} (${inner.names.join(', ')})`
            : p.name,
        );
      } else {
        names.push(p.name);
      }
    }
    return { names, byMass };
  };

  const top = describe(lines, new Set([productId]), 1);
  const allergens = ALLERGENS.filter((a) => found.has(a));
  const ingredients = top.names;
  const text =
    `Ingredients: ${ingredients.length > 0 ? ingredients.join(', ') : 'none listed'}.` +
    (allergens.length > 0
      ? ` Contains: ${allergens.map((a) => ALLERGEN_LABELS[a]).join(', ')}.`
      : ' No allergens declared by its ingredients.');
  return {
    ingredients,
    allergens,
    orderedByMass: top.byMass,
    unknownComponents: [...unknown].sort(),
    text,
  };
}
